import { useEffect, useMemo, useRef, useState } from 'react';
import {
  applyBrand,
  brandToken,
  createEmptyDoc,
  createShapeLayer,
  createTextLayer,
  type BrandKit,
  type TemplateDoc,
} from '@bannermatic/core';
import { DocPreview, useBuiltinFonts } from '../../components/DocPreview';
import { useT } from '../../lib/i18n';

/** Three brands, one template: the brand kit in action on the landing page. */
const BRANDS: Array<{ name: string; kit: BrandKit }> = [
  {
    name: 'NORDIC',
    kit: {
      colors: {
        primary: '#1F3A2E',
        secondary: '#2D5A45',
        accent: '#E9C46A',
        background: '#F4EFE6',
        text: '#1F3A2E',
      },
      fonts: { 'font-heading': { family: 'Playfair Display' }, 'font-body': { family: 'Lora' } },
      logos: {},
    },
  },
  {
    name: 'VOLT',
    kit: {
      colors: {
        primary: '#0B0F19',
        secondary: '#1E293B',
        accent: '#A3E635',
        background: '#FFFFFF',
        text: '#0B0F19',
      },
      fonts: { 'font-heading': { family: 'Unbounded' }, 'font-body': { family: 'Manrope' } },
      logos: {},
    },
  },
  {
    name: 'bloom',
    kit: {
      colors: {
        primary: '#BE185D',
        secondary: '#9D174D',
        accent: '#FBCFE8',
        background: '#FFF1F7',
        text: '#831843',
      },
      fonts: { 'font-heading': { family: 'Comfortaa' }, 'font-body': { family: 'Nunito' } },
      logos: {},
    },
  },
];

function templateFor(brandName: string, title: string, subtitle: string): TemplateDoc {
  const doc = createEmptyDoc(1080, 1080);
  doc.background = { type: 'solid', color: brandToken('primary', '#4F46E5') };
  doc.layers.push(
    createShapeLayer({
      name: 'circle',
      shape: 'ellipse',
      x: 560,
      y: -180,
      width: 720,
      height: 720,
      fill: { type: 'solid', color: brandToken('secondary', '#3730A3') },
    }),
    createTextLayer({
      name: 'brand',
      x: 80,
      y: 80,
      width: 600,
      height: 70,
      text: brandName,
      fontSize: 52,
      fontWeight: 700,
      letterSpacing: 6,
      fontFamily: brandToken('font-heading', 'Inter'),
      color: brandToken('accent', '#FACC15'),
    }),
    createTextLayer({
      name: 'title',
      x: 80,
      y: 520,
      width: 900,
      height: 260,
      text: title,
      fontSize: 112,
      fontWeight: 700,
      lineHeight: 1.05,
      fontFamily: brandToken('font-heading', 'Inter'),
      color: brandToken('background', '#FFFFFF'),
    }),
    createTextLayer({
      name: 'subtitle',
      x: 80,
      y: 800,
      width: 900,
      height: 60,
      text: subtitle,
      fontSize: 40,
      fontFamily: brandToken('font-body', 'Inter'),
      color: brandToken('background', '#FFFFFF'),
    }),
    createShapeLayer({
      name: 'badge-bg',
      shape: 'rectangle',
      x: 80,
      y: 920,
      width: 260,
      height: 90,
      borderRadius: 45,
      fill: { type: 'solid', color: brandToken('accent', '#FACC15') },
    }),
    createTextLayer({
      name: 'badge',
      x: 80,
      y: 935,
      width: 260,
      height: 60,
      text: '−30%',
      align: 'center',
      fontSize: 48,
      fontWeight: 800,
      fontFamily: brandToken('font-heading', 'Inter'),
      color: brandToken('text', '#111111'),
    }),
  );
  return doc;
}

export function BrandShowcase() {
  const t = useT();
  useBuiltinFonts();
  const title = t('landing.brandDemoTitle');
  const subtitle = t('landing.brandDemoSub');
  const docs = useMemo(
    () => BRANDS.map((b) => applyBrand(templateFor(b.name, title, subtitle), b.kit)),
    [title, subtitle],
  );
  return (
    <div className="grid grid-cols-3 gap-3 sm:gap-5">
      {BRANDS.map((b, i) => (
        <div key={b.name} className="min-w-0">
          <div className="overflow-hidden rounded-xl shadow-2xl ring-1 ring-white/10">
            <ResponsivePreview doc={docs[i]} />
          </div>
          <div className="mt-2.5 flex items-center justify-center gap-1">
            {(['primary', 'secondary', 'accent', 'background'] as const).map((slot) => (
              <span
                key={slot}
                className="size-3.5 rounded-full ring-1 ring-white/20"
                style={{ background: b.kit.colors[slot] }}
              />
            ))}
          </div>
          <div
            className="mt-1 truncate text-center text-[11px] text-zinc-400"
            style={{ fontFamily: `"${b.kit.fonts['font-heading']?.family}", sans-serif` }}
          >
            {b.kit.fonts['font-heading']?.family}
          </div>
        </div>
      ))}
    </div>
  );
}

/** DocPreview scaled to the column width. */
function ResponsivePreview({ doc }: { doc: TemplateDoc }) {
  const ref = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setBox(Math.floor(e.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="aspect-square w-full">
      {box > 0 && <DocPreview doc={doc} box={box} />}
    </div>
  );
}
